import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "../../Styles/DriftWall.css";

// Adapted from the reactbits.dev "Drift Wall" component
// (https://www.reactbits.dev/components/drift-wall), trimmed down for use as
// a decorative full-page background: no focusable/clickable tiles (a login
// page can't have 50+ hidden tab stops in front of the form) — the hover
// highlight/lift from the original is kept, but driven by plain CSS :hover
// instead of JS focus/activation state. Columns auto-fit the container width
// instead of a fixed count so the wall always fills the screen edge to edge.

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const columnFactor = (index, variance) => {
  const pseudo = ((index * 0.6180339887 + 0.35) % 1) * 2 - 1;
  return 1 + variance * pseudo;
};

const DriftWall = ({
  items,
  columns = "auto",
  tileWidth = 230,
  tileHeight = 410,
  gap = 20,
  radius = 16,
  tilt = 14,
  turn = -12,
  roll = 0,
  perspective = 1200,
  depth = 120,
  speed = 20,
  direction = "up",
  variance = 0.45,
  parallax = 0.5,
  dim = 0.9,
  lift = 64,
  grayscale = true,
  overlayColor = "var(--hc-driftwall-overlay-default)",
  fade = 0.05,
  className = "",
}) => {
  const containerRef = useRef(null);
  const planeRef = useRef(null);
  const trackRefs = useRef([]);
  const rafRef = useRef(null);

  const offsetsRef = useRef([]);
  const velocitiesRef = useRef([]);
  const pointerRef = useRef({ x: 0, y: 0 });
  const pointerDampedRef = useRef({ x: 0, y: 0 });
  const lastTsRef = useRef(null);

  const [containerSize, setContainerSize] = useState({ width: 1200, height: 600 });
  const [reduced, setReduced] = useState(false);
  // Tapping a tile (no real :hover on touch) toggles the same highlight a
  // mouse hover gives it, keyed by column/copy/item so only that one tile lights up.
  const [activeTileKey, setActiveTileKey] = useState(null);
  const handleTileClick = (key) => setActiveTileKey((prev) => (prev === key ? null : key));

  useEffect(() => {
    setReduced(prefersReducedMotion());
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerSize({
        width: entry.contentRect.width || 1200,
        height: entry.contentRect.height || 600,
      });
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Tiles shrink on narrow viewports so the wall still reads as a dense
  // mosaic instead of a couple of oversized cards. Derived once here (rather
  // than via a CSS breakpoint) so the JS loop/column math the animation
  // relies on never disagrees with what's actually rendered on screen.
  const tileScale = containerSize.width < 480 ? 0.62 : containerSize.width < 720 ? 0.8 : 1;
  const effectiveTileWidth = Math.round(tileWidth * tileScale);
  const effectiveTileHeight = Math.round(tileHeight * tileScale);
  const effectiveGap = Math.round(gap * tileScale);

  const resolvedColumns = useMemo(() => {
    if (typeof columns === "number") return Math.max(1, columns);
    const unit = effectiveTileWidth + effectiveGap;
    // The plane is scaled up (1.18x) and rotated for the perspective-wall
    // look, so the wall needs extra columns well beyond a flat width/unit
    // ratio to still cover every corner of the viewport with no bare edges.
    const needed = Math.ceil((containerSize.width * 1.8) / unit) + 3;
    return Math.max(4, needed);
  }, [columns, containerSize.width, effectiveTileWidth, effectiveGap]);

  const columnItems = useMemo(() => {
    const pool = items && items.length ? items : [];
    if (!pool.length) return Array.from({ length: resolvedColumns }, () => []);
    // Each column carries the full item list (rotated so every one of them
    // gets used). Column 0 starts at image 1, column 1 at image 3, column 2
    // at image 5, etc. — a fixed 2-image step per column, wrapping back
    // around once it runs past the end of the pool.
    return Array.from({ length: resolvedColumns }, (_, c) => {
      const offset = (c * 2) % pool.length;
      return pool.slice(offset).concat(pool.slice(0, offset));
    });
  }, [items, resolvedColumns]);

  const columnMeta = useMemo(() => {
    const unit = effectiveTileHeight + effectiveGap;
    return columnItems.map((col) => {
      const copyHeight = Math.max(unit, col.length * unit);
      // Generous coverage (well beyond the flat container height) so the
      // 3D tilt/perspective — which visually extends the wall past the
      // container's own bounds — never scrolls past the end of the
      // rendered copies and shows a bare gap mid-drift.
      const copies = Math.max(3, Math.ceil((containerSize.height * 3.5) / copyHeight) + 3);
      return { copyHeight, copies };
    });
  }, [columnItems, effectiveTileHeight, effectiveGap, containerSize.height]);

  const baseVelocities = useMemo(() => {
    const dirSign = direction === "up" ? 1 : -1;
    return columnItems.map((_, c) => {
      const altSign = c % 2 === 0 ? 1 : -1;
      return speed * columnFactor(c, variance) * dirSign * altSign;
    });
  }, [columnItems, speed, direction, variance]);

  useEffect(() => {
    offsetsRef.current = columnMeta.map((meta, c) => meta.copyHeight * ((c * 0.37) % 1));
    velocitiesRef.current = columnItems.map(() => 0);
  }, [columnMeta, columnItems]);

  useEffect(() => {
    if (reduced || parallax <= 0) return undefined;
    const handleMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || !rect.width || !rect.height) return;
      pointerRef.current = {
        x: (e.clientX - rect.left) / rect.width - 0.5,
        y: (e.clientY - rect.top) / rect.height - 0.5,
      };
    };
    const handleLeave = () => {
      pointerRef.current = { x: 0, y: 0 };
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerleave", handleLeave);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerleave", handleLeave);
    };
  }, [reduced, parallax]);

  useEffect(() => {
    const applyPlaneTransform = (px, py) => {
      const plane = planeRef.current;
      if (!plane) return;
      plane.style.transform =
        `translate(-50%, -50%) scale(1.18) ` +
        `rotateX(${tilt + py}deg) rotateY(${turn + px}deg) rotateZ(${roll}deg) ` +
        `translateZ(${-depth}px)`;
    };

    const animate = (ts) => {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const dt = Math.min(0.05, Math.max(0, ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      const maxTilt = parallax * 8;
      const targetX = pointerRef.current.x * maxTilt;
      const targetY = -pointerRef.current.y * maxTilt;
      const damp = 1 - Math.exp(-dt / 0.12);
      pointerDampedRef.current.x += (targetX - pointerDampedRef.current.x) * damp;
      pointerDampedRef.current.y += (targetY - pointerDampedRef.current.y) * damp;
      applyPlaneTransform(pointerDampedRef.current.x, pointerDampedRef.current.y);

      if (!reduced) {
        for (let c = 0; c < trackRefs.current.length; c++) {
          const meta = columnMeta[c];
          if (!meta) continue;
          const target = baseVelocities[c];
          const ease = 1 - Math.exp(-dt / 0.28);
          velocitiesRef.current[c] += (target - velocitiesRef.current[c]) * ease;
          let next = (offsetsRef.current[c] ?? 0) + velocitiesRef.current[c] * dt;
          next = ((next % meta.copyHeight) + meta.copyHeight) % meta.copyHeight;
          offsetsRef.current[c] = next;

          const el = trackRefs.current[c];
          if (el) el.style.transform = `translate3d(0, ${-next}px, 0)`;
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
    };
  }, [baseVelocities, columnMeta, parallax, reduced, tilt, turn, roll, depth]);

  const cssVars = useMemo(
    () => ({
      "--dw-tile-w": `${effectiveTileWidth}px`,
      "--dw-tile-h": `${effectiveTileHeight}px`,
      "--dw-gap": `${effectiveGap}px`,
      "--dw-radius": `${radius}px`,
      "--dw-perspective": `${perspective}px`,
      "--dw-dim": dim,
      "--dw-lift": `${lift}px`,
      "--dw-gray": grayscale ? 1 : 0,
      "--dw-overlay": overlayColor,
      "--dw-edge": `${Math.max(0, (1 - fade) * 100)}%`,
    }),
    [
      effectiveTileWidth,
      effectiveTileHeight,
      effectiveGap,
      radius,
      perspective,
      dim,
      lift,
      grayscale,
      overlayColor,
      fade,
    ]
  );

  const rootClass = ["drift-wall position-absolute w-100 h-100 overflow-hidden pe-none", reduced ? "drift-wall--reduced" : "", className].filter(Boolean).join(" ");

  return (
    <div ref={containerRef} className={rootClass} style={cssVars} aria-hidden="true">
      <div ref={planeRef} className="drift-wall__plane position-absolute d-flex flex-row">
        {columnItems.map((col, c) => {
          const meta = columnMeta[c];
          const copies = Array.from({ length: meta.copies });
          return (
            <div className="drift-wall__col position-relative" key={`col-${c}`}>
              <div className="drift-wall__track d-flex flex-column" ref={(el) => (trackRefs.current[c] = el)}>
                {copies.map((_, copyIndex) =>
                  col.map((item, itemIndex) => {
                    const tileKey = `${c}-${copyIndex}-${itemIndex}`;
                    return (
                      <div
                        className={`drift-wall__tile position-relative d-block w-100 pe-auto${activeTileKey === tileKey ? " drift-wall__tile--active" : ""}`}
                        key={tileKey}
                        onClick={() => handleTileClick(tileKey)}
                      >
                        <span className="drift-wall__inner position-absolute d-block overflow-hidden pe-none">
                          <img src={item.image} alt="" loading="lazy" decoding="async" draggable={false} className="w-100 h-100 object-fit-cover d-block user-select-none" />
                          <span className="drift-wall__overlay position-absolute pe-none" />
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DriftWall;
